import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import HostelCard from "@/components/HostelCard";
import InstallAppButton from "@/components/InstallAppButton";
import ShareQRCard from "@/components/ShareQRCard";
import { Button } from "@/components/ui/button";
import { ShieldCheck, IndianRupee, GraduationCap, Wifi, Utensils, KeyRound, ArrowRight, Sparkles } from "lucide-react";

const COACHINGS = [
  { name: "Allen", city: "Kota" },
  { name: "Aakash", city: "Kota" },
  { name: "Career Point", city: "Jaipur" },
  { name: "Utkarsh Classes", city: "Jaipur" },
  { name: "Matrix", city: "Sikar" },
  { name: "PCP", city: "Sikar" },
  { name: "Vajiram & Ravi", city: "Delhi" },
  { name: "Drishti IAS", city: "Delhi" },
  { name: "Naresh IT", city: "Hyderabad" },
  { name: "BYJU'S", city: "Bangalore" },
  { name: "Shankar IAS", city: "Chennai" },
  { name: "Made Easy", city: "Delhi" },
];

export default function Home() {
  const { user } = useAuth();
  const [hostels, setHostels] = useState([]);
  useEffect(() => { api.get("/hostels").then((r) => setHostels(r.data)); }, []);

  return (
    <div className="min-h-screen bg-[#FBF5EA] overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-x-clip">
        {/* Decorative jharokha (palace arch) top border — Rajasthani motif */}
        <div className="jharokha-border h-3 w-full" aria-hidden />

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-24 -right-24 w-[280px] h-[280px] md:w-[520px] md:h-[520px] rounded-full bg-marigold-100 blur-[80px] opacity-70" />
          <div className="absolute top-40 -left-24 w-[220px] h-[220px] md:w-[420px] md:h-[420px] rounded-full bg-jodhpur-100 blur-[80px] opacity-70" />
          {/* Bandhani dot pattern layer */}
          <div className="bandhani-bg absolute inset-0" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-14 sm:pb-16 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 min-w-0">
            <div className="rise inline-flex items-center gap-2 bg-white border border-marigold-200 rounded-full px-3 py-1.5 text-[10px] sm:text-xs tracking-widest uppercase font-bold text-vermilion-600 shadow-sm max-w-full">
              <Sparkles size={12} className="flex-shrink-0" />
              <span className="truncate">India's student hostel network · Beds from ₹250/night</span>
            </div>
            <h1 data-testid="hero-title" className="rise rise-1 mt-5 sm:mt-6 font-display font-black text-[2.25rem] xs:text-[2.5rem] sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02] sm:leading-[0.95] break-words">
              Looking for a <span className="text-vermilion-600">hostel?</span>
              <br /> Your search <span className="text-jodhpur-700">ends here!</span>
            </h1>
            <div className="mt-3 text-base sm:text-xl font-display font-bold tracking-tight text-stone-700 flex items-center gap-2 flex-wrap">
              <span className="mehndi-divider inline-flex items-center">
                TheHostel<span className="text-vermilion-600">Now</span><span className="text-turmeric-500">.com</span>
              </span>
            </div>
            <p data-testid="hero-tagline" className="rise rise-2 mt-4 sm:mt-5 font-display font-black text-xl sm:text-2xl tracking-tight text-stone-800 max-w-xl">
              Verified Stays. <span className="text-vermilion-600">No Hidden Charges.</span> Ever.
            </p>
            <p className="mt-3 text-base sm:text-lg text-slate-700 max-w-xl leading-relaxed">
              Safe, budget-friendly beds near every major coaching center across India — NEET, JEE, UPSC, CET, SSC, Banking, GATE, CAT. The price you see is the price you pay.
            </p>

            {/* ₹7,000 all-inclusive Monthly Saver — flagship offer */}
            <div
              data-testid="monthly-saver-hero-callout"
              className="rise rise-3 mt-6 relative overflow-hidden flex flex-col sm:inline-flex sm:flex-row items-stretch gap-0 rounded-2xl shadow-lg border-2 border-turmeric-400 max-w-full w-full sm:w-auto card-lift"
            >
              <div className="bg-peacock-gradient text-white px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3">
                <div className="grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-turmeric-400 text-jodhpur-900 font-black text-base sm:text-lg shadow-inner flex-shrink-0">
                  ₹
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-bold text-turmeric-300">Monthly Saver</div>
                  <div className="font-display font-black text-xl sm:text-2xl leading-tight">
                    ₹7,000 <span className="text-turmeric-300 text-base sm:text-lg font-bold">/ month</span>
                  </div>
                </div>
              </div>
              <div className="bg-white px-4 sm:px-5 py-3 sm:py-4 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-peacock-700 uppercase tracking-widest">
                  <Utensils size={13} /> 2 meals included
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">Bed + lunch + dinner · 30-night plan</div>
              </div>
              {/* Corner ribbon */}
              <div className="absolute top-0 right-0 bg-bandhani-600 text-white text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded-bl-lg">Save 22%</div>
            </div>

            {/* Yearly saver mini-chip — added Aug 2026 */}
            <div
              data-testid="yearly-saver-hero-chip"
              className="mt-3 inline-flex items-center gap-2 bg-white border-2 border-turmeric-400 rounded-full px-3 py-1.5 text-xs font-bold text-vermilion-700 shadow-sm"
            >
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-turmeric-400 text-jodhpur-900 text-[10px] font-black">%</span>
              <span>Yearly plan · <b className="text-emerald-700">15% off</b> for 365+ nights</span>
            </div>


            {/* Primary CTAs — visible immediately, no need to scroll to the top navbar */}
            {!user && (
              <div className="rise rise-4 mt-6 sm:mt-7 flex flex-wrap gap-3">
                <Link to="/signup" className="flex-1 sm:flex-none min-w-0">
                  <Button
                    data-testid="hero-get-started-btn"
                    className="btn-pop w-full sm:w-auto rounded-full h-12 px-6 sm:px-7 bg-orange-800 hover:bg-orange-900 text-white font-semibold text-base shadow-lg shadow-orange-800/25 hover:shadow-xl hover:shadow-orange-800/35 gap-2"
                  >
                    Get started free <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/login" className="flex-1 sm:flex-none min-w-0">
                  <Button
                    data-testid="hero-login-btn"
                    variant="outline"
                    className="btn-pop w-full sm:w-auto rounded-full h-12 px-6 border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold text-base"
                  >
                    Log in
                  </Button>
                </Link>
              </div>
            )}

            {/* Exam category chips */}
            <div className="rise rise-5 mt-5 sm:mt-6 flex flex-wrap gap-2">
              {[
                { label: "NEET / JEE", to: "/hostels?exam=NEET" },
                { label: "UPSC / IAS", to: "/hostels?exam=UPSC" },
                { label: "CET / State PSC", to: "/hostels?exam=CET" },
                { label: "SSC / Banking", to: "/hostels?exam=SSC" },
                { label: "GATE / CAT", to: "/hostels?exam=GATE" },
                { label: "Defence (NDA/CDS)", to: "/hostels?exam=Defence" },
                { label: "Any exam", to: "/hostels" },
              ].map((e) => (
                <Link
                  key={e.label}
                  to={e.to}
                  data-testid={`exam-chip-${e.label}`}
                  className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold bg-white border border-orange-200 text-orange-800 rounded-full px-2.5 sm:px-3 py-1.5 hover:bg-orange-50 hover:border-orange-400 hover:-translate-y-0.5 transition-[background-color,border-color,transform] duration-200"
                >
                  <GraduationCap size={12} className="flex-shrink-0" /> {e.label}
                </Link>
              ))}
            </div>

            <div className="mt-6 sm:mt-10 max-w-3xl">
              <SearchBar />
            </div>

            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3">
              <InstallAppButton variant="hero" />
              <div className="hidden sm:flex items-center gap-6 text-sm ml-2">
                <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-orange-800" /> Aadhaar KYC verified</div>
                <div className="flex items-center gap-2"><IndianRupee size={16} className="text-amber-600" /> No hidden fees</div>
                <div className="flex items-center gap-2"><GraduationCap size={16} className="text-orange-800" /> For every student, any exam</div>
              </div>
            </div>
            <div className="sm:hidden mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600">
              <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-orange-800" /> Aadhaar KYC</div>
              <div className="flex items-center gap-1.5"><IndianRupee size={14} className="text-amber-600" /> No hidden fees</div>
              <div className="flex items-center gap-1.5"><GraduationCap size={14} className="text-orange-800" /> Any exam</div>
            </div>
          </div>

          {/* Image bento */}
          <div className="lg:col-span-5 relative h-[480px] hidden lg:block">
            <div className="absolute top-0 right-8 w-64 h-72 rounded-3xl overflow-hidden shadow-xl border border-slate-200 rotate-3 hover:rotate-1 hover:scale-[1.03] transition-transform duration-500 ease-out">
              <img src="https://images.unsplash.com/photo-1555854877-bab0e564b8d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMGhvc3RlbCUyMGJ1bmslMjBiZWQlMjBkb3JtfGVufDB8fHx8MTc4MzY1OTk4NHww&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt="dorm" />
            </div>
            <div className="absolute bottom-0 left-0 w-72 h-56 rounded-3xl overflow-hidden shadow-xl border border-slate-200 -rotate-2 hover:rotate-0 hover:scale-[1.03] transition-transform duration-500 ease-out">
              <img src="https://images.unsplash.com/photo-1709805619372-40de3f158e83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMGhvc3RlbCUyMGJ1bmslMjBiZWQlMjBkb3JtfGVufDB8fHx8MTc4MzY1OTk4NHww&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt="dorm" />
            </div>
            <div className="absolute top-32 left-8 w-52 h-52 rounded-3xl overflow-hidden shadow-xl border border-slate-200 rotate-6 hover:rotate-3 hover:scale-[1.03] transition-transform duration-500 ease-out">
              <img src="https://images.unsplash.com/photo-1549881567-c622c1080d78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjbGVhbiUyMGhvc3RlbCUyMGJ1bmslMjBiZWQlMjBkb3JtfGVufDB8fHx8MTc4MzY1OTk4NHww&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt="dorm" />
            </div>
            <div className="absolute bottom-16 right-0 bg-white rounded-2xl shadow-2xl border border-turmeric-400 p-4 w-56 card-lift">
              <div className="text-xs uppercase tracking-widest font-bold text-vermilion-600">All-inclusive plan</div>
              <div className="font-display font-black text-3xl mt-1 flex items-baseline text-jodhpur-800"><IndianRupee size={20} strokeWidth={3} />7,000<span className="text-xs text-slate-500 ml-1 font-body font-normal">/mo</span></div>
              <div className="text-xs text-slate-700 mt-1 flex items-center gap-1"><Utensils size={11} className="text-peacock-600" /> Bed + lunch + dinner daily</div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-t border-b border-slate-200 bg-white overflow-hidden">
          <div className="ticker flex whitespace-nowrap py-3 text-sm font-semibold text-slate-700">
            {[...COACHINGS, ...COACHINGS].map((c, i) => (
              <span key={`${c.name}-${i}`} className="mx-8 inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-600" /> {c.name} — {c.city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured hostels */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Handpicked</div>
            <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter">Trusted by aspirants of every exam</h2>
          </div>
          {/* Desktop / tablet — big pill button (replaces the old text link) */}
          <Link
            to="/hostels"
            data-testid="desktop-view-all-hostels"
            className="btn-pop hidden sm:inline-flex items-center gap-2 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-12 px-6 text-base font-semibold shadow-lg shadow-orange-800/25 hover:shadow-xl hover:shadow-orange-800/35 whitespace-nowrap"
          >
            View all hostels <ArrowRight size={18} />
          </Link>
        </div>
        {/* Mobile: full scrollable list (all hostels stacked) */}
        <div className="grid grid-cols-1 gap-5 sm:hidden">
          {hostels.map((h) => <HostelCard key={h.id} h={h} />)}
        </div>
        {/* Tablet/Desktop: curated 6 in a grid */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hostels.slice(0, 6).map((h) => <HostelCard key={h.id} h={h} />)}
        </div>
        {/* Big "View all" CTA below the grid — visible on ALL breakpoints */}
        <div className="mt-10 flex justify-center">
          <Link
            to="/hostels"
            data-testid="mobile-view-all-hostels"
            className="btn-pop inline-flex items-center justify-center gap-3 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-14 px-8 text-base sm:text-lg font-bold shadow-xl shadow-orange-800/30 hover:shadow-2xl hover:shadow-orange-800/40 w-full sm:w-auto"
          >
            {hostels.length > 6 ? `View all ${hostels.length} hostels` : "Browse all hostels"} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Rajasthan coaching cluster */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Rajasthan NEET/JEE belt</div>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter">India's coaching capital.</h2>
            <p className="mt-2 text-slate-600 max-w-xl">Six cities. Hundreds of institutes. Beds starting at ₹250/night.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { c: "Kota", tag: "Allen · Aakash", hue: "from-orange-700 to-orange-800" },
            { c: "Jaipur", tag: "Career Point · Utkarsh", hue: "from-pink-500 to-rose-600" },
            { c: "Sikar", tag: "Matrix · PCP", hue: "from-amber-600 to-red-500" },
            { c: "Ajmer", tag: "Allen · Career Point", hue: "from-emerald-500 to-teal-600" },
            { c: "Udaipur", tag: "Career Point · Rays IAS", hue: "from-cyan-500 to-sky-700" },
            { c: "Jodhpur", tag: "Utkarsh · RAS Guru", hue: "from-amber-500 to-amber-700" },
          ].map((x) => (
            <Link
              key={x.c}
              to={`/${x.c.toLowerCase()}`}
              data-testid={`rj-city-${x.c}`}
              className={`card-lift group relative overflow-hidden rounded-2xl aspect-[4/5] bg-gradient-to-br ${x.hue} text-white p-4 flex flex-col justify-between shadow-md`}
            >
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold opacity-90">Rajasthan</div>
              <div>
                <div className="font-display font-black text-2xl leading-none tracking-tight">{x.c}</div>
                <div className="mt-1 text-[11px] font-medium opacity-90 line-clamp-1">{x.tag}</div>
              </div>
              <ArrowRight size={16} className="absolute bottom-4 right-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">How it works</div>
          <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter max-w-2xl">Book a verified bed in three steps.</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { icon: <KeyRound size={24} />, n: "01", t: "Search & pick", d: "Filter by your exam, coaching, city, budget & gender preference — NEET, UPSC, CET, SSC, GATE, and more." },
              { icon: <ShieldCheck size={24} />, n: "02", t: "Verify KYC", d: "Upload your Aadhaar + share parent/guardian contact. Encrypted & private." },
              { icon: <IndianRupee size={24} />, n: "03", t: "Pay & move in", d: "Instant confirmation. Beds from ₹250/night. Cancel anytime before check-in." },
            ].map((s) => (
              <div key={s.n} className="card-lift bg-[#FBF5EA] rounded-3xl border border-slate-200 p-8 hover:border-orange-600">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-orange-800 text-white grid place-items-center">{s.icon}</div>
                  <span className="font-display text-6xl font-black text-slate-200">{s.n}</span>
                </div>
                <h3 className="mt-6 font-display font-bold text-2xl">{s.t}</h3>
                <p className="mt-2 text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Share via QR */}
      <ShareQRCard />

      {/* Owner CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-stone-900 text-white p-10 sm:p-16 border border-stone-800">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-orange-800 blur-[100px] opacity-60" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-500">For property owners</div>
              <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter">Own or lease a property? Upload your hostel. Earn on empty beds.</h2>
              <p className="mt-4 text-slate-300 max-w-md">Sign up in 60 seconds, upload photos, set your price and gender policy — your listing goes live after admin verification. <span className="font-bold text-white">Zero platform fees, zero commission</span> — students pay exactly your listed price and you keep 100% of what you charge.</p>
            </div>
            <div className="flex flex-col sm:flex-row md:justify-end gap-3">
              <Link to="/signup?role=owner">
                <Button data-testid="cta-owner" className="btn-pop w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white h-12 px-6 font-semibold shadow-lg shadow-amber-600/25">
                  List your hostel <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="cta-owner-login" variant="outline" className="w-full rounded-full h-12 px-6 font-semibold bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white">
                  Owner login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
